import { useImperativeHandle, forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, SubmitHandler } from 'react-hook-form';
import './AccountStep.scss';
import dangerIcon from '@/assets/icons/danger-icon.svg';
// Using inline validation instead of imported validation

export interface AccountFormData {
  firstName: string;
  lastName: string;
  professionalEmail: string;
  personalEmail: string;
  website: string;
  instagram: string;
  linkedin: string;
  profession: string;
}

export interface AccountStepRef {
  submitForm: () => Promise<boolean>;
  isValid: () => boolean;
  getFormData: () => AccountFormData;
}

interface AccountStepProps {
  onNext: SubmitHandler<AccountFormData>;
  initialData?: Partial<AccountFormData>;
  // Removed onPrevious and showPrevious as they're now handled in RegistrationPage
}

export const AccountStep = forwardRef<AccountStepRef, AccountStepProps>(({ onNext, initialData }, ref) => {
  const { t } = useTranslation('auth');
  const { register, handleSubmit, formState: { errors }, getValues, watch, trigger } = useForm<AccountFormData>({
    defaultValues: initialData,
    mode: 'onChange' // Enable validation on change
  });
  
  // State to track if form submission was attempted
  const [submissionAttempted, setSubmissionAttempted] = useState(false);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    submitForm: async () => {
      setSubmissionAttempted(true);
      
      // Trigger validation for all fields
      const isValid = await trigger();
      
      // Check if at least one profile URL is provided
      const hasProfileUrl = Boolean(getValues().website || getValues().instagram || getValues().linkedin);
      
      // Return false if validation failed or no profile URL
      if (!isValid || !hasProfileUrl) {
        return false;
      }
      
      // All validations passed
      handleSubmit(onNext)();
      return true;
    },
    isValid: () => {
      const values = getValues();
      const hasProfileUrl = Boolean(values.website || values.instagram || values.linkedin);
      return Object.keys(errors).length === 0 && hasProfileUrl;
    },
    getFormData: () => {
      return getValues();
    }
  }));

  return (
    <div className="account-step">
      <div className="account-step__header">
        <h2 className="account-step__title">{t('registration.account.title')}</h2>
        <p className="account-step__description">{t('registration.account.description')}</p>
      </div>

      <form onSubmit={(e) => e.preventDefault()} className="account-step__form">
        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label required" htmlFor="firstName">
              {t('registration.account.firstName')}
            </label>
            <input
              type="text"
              id="firstName"
              {...register('firstName', { required: true })}
              className={`form-field__input ${errors.firstName ? 'error' : ''}`}
            />
            {errors.firstName && <span className="form-field__error">{t('validation.required')}</span>}
          </div>

          <div className="form-field">
            <label className="form-field__label required" htmlFor="lastName">
              {t('registration.account.lastName')}
            </label>
            <input
              type="text"
              id="lastName"
              {...register('lastName', { required: true })}
              className={`form-field__input ${errors.lastName ? 'error' : ''}`}
            />
            {errors.lastName && <span className="form-field__error">{t('validation.required')}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label required" htmlFor="professionalEmail">
              {t('registration.account.professionalEmail')}
            </label>
            <input
              type="email"
              id="professionalEmail"
              {...register('professionalEmail', { 
                required: true, 
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('validation.email')
                }
              })}
              className={`form-field__input ${errors.professionalEmail ? 'error' : ''}`}
            />
            {errors.professionalEmail && <span className="form-field__error">{errors.professionalEmail.message || t('validation.required')}</span>}
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="personalEmail">
              {t('registration.account.personalEmail')}
            </label>
            <input
              type="email"
              id="personalEmail"
              {...register('personalEmail', {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('validation.email')
                }
              })}
              className={`form-field__input ${errors.personalEmail ? 'error' : ''}`}
            />
            {errors.personalEmail && <span className="form-field__error">{errors.personalEmail.message}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label" htmlFor="website">
              {t('registration.account.websitePortfolio')}
            </label>
            <input
              type="url"
              id="website"
              {...register('website', {
                validate: (value) => {
                  if (!value) return true; // Optional field
                  // URL pattern: must start with http://, https://, or www., or be a valid domain
                  const urlPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
                  return urlPattern.test(value) || t('validation.website');
                }
              })}
              className={`form-field__input ${errors.website || (submissionAttempted && !watch('website') && !watch('instagram') && !watch('linkedin')) ? 'error' : ''}`}
            />
            {errors.website && <span className="form-field__error">{errors.website.message}</span>}
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="instagram">
              {t('registration.account.instagramProfile')}
            </label>
            <input
              type="text"
              id="instagram"
              {...register('instagram', {
                validate: (value) => {
                  if (!value) return true; // Optional field
                  // Instagram URL pattern: must contain instagram.com
                  const instagramPattern = /^(https?:\/\/)?(www\.)?(instagram\.com|instagr\.am)\/.+$/i;
                  return instagramPattern.test(value) || t('validation.instagram');
                }
              })}
              className={`form-field__input ${errors.instagram || (submissionAttempted && !watch('website') && !watch('instagram') && !watch('linkedin')) ? 'error' : ''}`}
            />
            {errors.instagram && <span className="form-field__error">{errors.instagram.message}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label" htmlFor="linkedin">
              {t('registration.account.linkedinProfile')}
            </label>
            <input
              type="url"
              id="linkedin"
              {...register('linkedin', {
                validate: (value) => {
                  if (!value) return true; // Optional field
                  // LinkedIn URL pattern: must contain linkedin.com
                  const linkedinPattern = /^(https?:\/\/)?(www\.)?linkedin\.com\/.+$/i;
                  return linkedinPattern.test(value) || t('validation.linkedin');
                }
              })}
              className={`form-field__input ${errors.linkedin || (submissionAttempted && !watch('website') && !watch('instagram') && !watch('linkedin')) ? 'error' : ''}`}
            />
            {errors.linkedin && <span className="form-field__error">{errors.linkedin.message}</span>}
          </div>

          <div className="form-field">
            <label className="form-field__label required" htmlFor="profession">
              {t('registration.account.profession')}
            </label>
            <div className="form-field__select-wrapper">
              <select
                id="profession"
                {...register('profession', { required: true })}
                className={`form-field__select ${errors.profession ? 'error' : ''}`}
              >
                <option value="">{t('registration.account.selectProfession')}</option>
                <option value="Architect">{t('registration.account.professions.architect')}</option>
                <option value="Interior Designer">{t('registration.account.professions.interiorDesigner')}</option>
              </select>
            </div>
            {errors.profession && <span className="form-field__error">{t('validation.required')}</span>}
          </div>
        </div>

        {submissionAttempted && !watch('website') && !watch('instagram') && !watch('linkedin') && (
          <div className="profile-error-container">
            <div className="profile-error-message">
              <img src={dangerIcon} alt="Warning" className="error-icon" />
              <span>{t('registration.error.profileRequired') || "Please provide at least one link to your online profiles"}</span>
            </div>
          </div>
        )}

      </form>
    </div>
  );
});

// Add display name for better debugging
AccountStep.displayName = 'AccountStep';
